import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Link2, Linkedin, Facebook, Twitter } from "lucide-react";
import { toast } from "sonner";
import { IMAGES, imgUrl } from "@/data/content";
import Newsletter from "@/components/Newsletter";

const BODY_SECTIONS = [
  {
    "heading": "What Has Been Announced",
    "paragraphs": [
      "President John Dramani Mahama's 2–5 August 2026 visit to Jamaica followed a concrete diplomatic reopening: on 26 May, Ghana and Jamaica reconvened their Permanent Joint Commission for Cooperation in Accra after 21 years. The governments signed defence and health cooperation instruments and exchanged notes amending an arts-and-culture agreement. They also identified trade, air services and education as priority areas. That distinction matters: some instruments were signed, while other proposals were statements of intent. [1, 2]",
      "During talks with Prime Minister Andrew Holness on 3 August, Mahama renewed discussion of an air link between Ghana and Jamaica. Their agenda included trade, tourism, education, culture and agriculture. The Ghanaian Presidency described an ambition to build economic and transport links across the Atlantic, but an ambition is not an operating airline, a financed route or a published timetable. [3, 4]",
      "Reparatory justice was also a stated focus of the presidential visit, including dialogue at the University of the West Indies, Mona. This can support collaboration in archives, education and diplomacy, but the available public records do not by themselves establish a funded bilateral reparations programme. [5]"
    ]
  },
  {
    "heading": "What Has Actually Been Implemented",
    "paragraphs": [
      "An important milestone is now verifiable. In late September 2026, Jamaica welcomed 43 specialist nurses from Ghana. The Health Ministry confirmed the first cohort in a 29 September release, and a 12 October government update says they have since been deployed across health regions [8]. They constitute the first cohort under the bilateral health cooperation initiative; the programme targets 400 healthcare workers. This is a delivered movement of personnel, not merely a signed promise. [6]",
      "The original May agreement establishes a four-year framework for voluntary, temporary recruitment, with both governments presenting it as an ethical workforce arrangement. Jamaica says a joint implementation and monitoring committee will oversee recruitment, deployment, welfare and the programme. Those safeguards should be assessed against actual contracts, working conditions, retention and the implications for Ghana's own health workforce. [7]",
      "Earlier disaster cooperation offers a second concrete reference point: Ghana deployed military engineering support after Hurricane Melissa, a contribution acknowledged in the May 2026 diplomatic record. The question for future work is whether that emergency assistance becomes regular, reciprocal preparedness training rather than a one-off intervention. [1]"
    ]
  },
  {
    "heading": "What Remains Unresolved",
    "paragraphs": [
      "Air connectivity still requires evidence of an operator, route economics, regulatory clearances, cargo demand and funding. Jamaica announced a proposed export and business mission to Ghana for July 2026, involving more than 38 companies; that announcement does not prove the mission occurred or that commercial deals followed. Until independently documented, both the mission's outcomes and the proposed air link should remain under verification. [2]",
      "Education and cultural cooperation require the same discipline. The Joint Commission set education on its agenda and amended a cultural agreement, but publicly specified universities, exchanges, budgets and delivery dates have not been established by the government records consulted for this analysis. Public optimism must not be mistaken for implemented programmes. [1]",
      "Accountability is a practical problem, not an adversarial demand. Without named implementing agencies, milestones, financing and reporting, neither citizens nor businesses can distinguish sustained progress from a cycle of ceremonies and communiqués."
    ]
  },
  {
    "heading": "From Symbolism to Measurable Cooperation",
    "paragraphs": [
      "Each bilateral commitment needs an implementation record with five minimum fields: a named responsible institution; the precise commitment; its agreed target or deadline; the documentary evidence of progress; and the next unresolved question. The record should preserve the difference between a ministerial announcement, a signed instrument, an activity underway and a completed result.",
      "Health-worker cooperation shows why this framework is useful. The May agreement was a commitment; the late-September arrival and subsequent regional deployment of 43 nurses were measurable implementation steps. The next questions concern subsequent arrivals, welfare monitoring, compensation, knowledge exchange and workforce sustainability. A target of 400 is a programme ambition, not a claim that 400 workers have arrived. [6, 7]",
      "A similar test can be applied to trade: name the firms and activities involved, record any completed missions, publish market-entry obstacles and document actual transactions. In aviation, count routes and operated flights, not speeches about connectivity."
    ]
  },
  {
    "heading": "What Atlantic Bridge Will Track",
    "paragraphs": [
      "The Institute's Ghana–Jamaica Cooperation Tracker distinguishes announced measures, planning, implementation and completion across six sectors: air connectivity, trade and investment, health workforce, education, culture and creative industries, and reparatory justice. Entries carry primary-source links and a date of verification. Where evidence is missing, the status remains explicit rather than filled by inference.",
      "Atlantic Bridge will seek clarification from governments, universities, professional bodies, businesses and affected workers. Public documents and on-the-record information will update the record; institutions named in the tracker are not presented as partners or endorsers of this project. Corrections and documentary evidence can be submitted through the Institute's contact form.",
      "The central test of the Ghana–Jamaica relationship is no longer whether leaders can articulate a shared Atlantic past. It is whether their citizens can point to functioning institutions, fair exchanges, real travel links, durable commercial ties and public evidence of results. The arrival of 43 nurses is a beginning; it is not the whole bridge."
    ]
  }
];

const SOURCES = [
  {
    "label": "[1] Ghana Ministry of Foreign Affairs — Third Permanent Joint Commission (4 June 2026)",
    "url": "https://mfa.gov.gh/index.php/ministerial-session-of-the-third-permanent-joint-commission-for-cooperation-between-ghana-and-jamaica/"
  },
  {
    "label": "[2] Jamaica Ministry of Foreign Affairs — Joint Commission outcomes (3 June 2026)",
    "url": "https://mfaft.gov.jm/site/jamaica-ghana-set-new-course-for-cooperation-after-two-decadesand/"
  },
  {
    "label": "[3] Ghana Ministry of Foreign Affairs — Mahama–Holness talks (4 August 2026)",
    "url": "https://mfa.gov.gh/index.php/as-part-of-his-historic-state-visit-to-jamaica-president-john-dramani-mahama-held-bilateral-talks-on-monday-3rd-august-2026-with-the-most-honourable-dr-andrew-holness-prime-minister-of-jamaica/"
  },
  {
    "label": "[4] Ghana Presidency — Economic and transport links (3 August 2026)",
    "url": "https://presidency.gov.gh/ghana-and-jamaica-pledge-stronger-economic-and-transport-links-to-bridge-the-atlantic/"
  },
  {
    "label": "[5] Ghana Presidency — Official Jamaica visit statement (30 July 2026)",
    "url": "https://presidency.gov.gh/statement-president-mahama-to-undertake-historic-official-visit-to-jamaica/"
  },
  {
    "label": "[6] Jamaica Ministry of Health — Confirmation of first 43 Ghanaian nurses (29 September 2026)",
    "url": "https://dashboard.moh.gov.jm/blogs/jamaica-welcomes-first-cohort-of-ghanaian-healthcare-workers/"
  },
  {
    "label": "[7] Jamaica Ministry of Health — Four-year health cooperation agreement (27 May 2026)",
    "url": "https://dashboard.moh.gov.jm/blogs/jamaica-signs-mou-with-ghana-for-health-co-operation/"
  }
,
  {
    "label": "[8] Jamaica Information Service — October update on regional deployment (12 October 2026)",
    "url": "https://jis.gov.jm/govt-expanding-recruitment-of-specialist-healthcare-professionals/"
  }];

const RELATED = [
  { label: "Dossier", title: "Jamaica Country Dossier", to: "/countries/jamaica" },
  { label: "Tracker", title: "Ghana–Jamaica Cooperation Tracker", to: "/research/cooperation-tracker" },
  { label: "Briefing", title: "Health Workforce Migration Across the Atlantic", to: "/research" },
];

export default function ArticlePage() {
  const url = typeof window !== "undefined" ? window.location.href : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard.");
    } catch (e) {
      toast.error("Could not copy the link.");
    }
  };

  const shareBtn =
    "flex h-10 w-10 items-center justify-center border border-atlantic/15 text-atlantic/70 transition-colors hover:border-gold hover:text-gold";

  return (
    <main data-testid="article-page" className="bg-sand">
      <div className="border-b border-atlantic/10 bg-sand-dark">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-5 py-4 lg:px-8">
          <Link to="/" data-testid="article-back-home" className="group flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-atlantic/70 transition-colors hover:text-ocean">
            <ArrowLeft size={14} className="transition-transform duration-300 group-hover:-translate-x-1" /> Back to Home
          </Link>
          <Link to="/#news" data-testid="article-all-news" className="text-xs font-semibold uppercase tracking-[0.2em] text-atlantic/70 transition-colors hover:text-ocean">
            All News & Analysis
          </Link>
        </div>
      </div>

      <article className="mx-auto max-w-4xl px-5 py-16 lg:px-8 lg:py-20">
        <p data-testid="article-category" className="text-xs font-semibold uppercase tracking-[0.28em] text-ocean">
          Ghana–Jamaica Relations
        </p>
        <h1 data-testid="article-headline" className="mt-5 font-serif text-3xl leading-tight tracking-tight text-atlantic sm:text-4xl lg:text-5xl">
          From Barrier to Bridge: What Ghana and Jamaica Must Build After the Presidential Visit
        </h1>
        <p data-testid="article-standfirst" className="mt-7 font-serif text-lg italic leading-relaxed text-slate-600 sm:text-xl">
          Ghana and Jamaica are renewing cooperation across health, transport, trade, education and culture.
          Forty-three Ghanaian nurses have now been deployed in Jamaica. Our evidence-based review separates
          this concrete progress from agreements, proposals and unresolved implementation questions.
        </p>

        <div className="mt-9 flex flex-col gap-5 border-y border-atlantic/10 py-5 sm:flex-row sm:items-center sm:justify-between">
          <div data-testid="article-meta" className="text-sm text-slate-600">
            <p className="font-semibold text-atlantic">Victor Kwawukume</p>
            <p className="mt-0.5 text-xs text-slate-500">Founder and Editor-in-Chief · Updated 15 October 2026 · Document-based analysis</p>
          </div>
          <div className="flex items-center gap-2.5" data-testid="article-share-controls">
            <span className="mr-1 text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500">Share</span>
            <button data-testid="share-copy-link" onClick={copyLink} aria-label="Copy article link" className={shareBtn}>
              <Link2 size={15} />
            </button>
            <a data-testid="share-linkedin" href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`} target="_blank" rel="noopener noreferrer" aria-label="Share on LinkedIn" className={shareBtn}>
              <Linkedin size={15} />
            </a>
            <a data-testid="share-facebook" href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`} target="_blank" rel="noopener noreferrer" aria-label="Share on Facebook" className={shareBtn}>
              <Facebook size={15} />
            </a>
            <a data-testid="share-x" href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}`} target="_blank" rel="noopener noreferrer" aria-label="Share on X" className={shareBtn}>
              <Twitter size={15} />
            </a>
          </div>
        </div>

        <figure className="mt-12">
          <img
            src={imgUrl(IMAGES.aviation, 1600)}
            alt="Airplane wing above the clouds — direct air connectivity between Africa and the Caribbean"
            className="aspect-[16/9] w-full object-cover"
          />
          <figcaption className="mt-3 text-xs italic text-slate-500">
            Direct air connectivity sits at the centre of the Ghana–Jamaica agenda. Illustrative aviation photograph, not an image of the presidential visit.
          </figcaption>
        </figure>

        <div className="mt-12 space-y-14">
          {BODY_SECTIONS.slice(0, 2).map((s) => (
            <section key={s.heading} data-testid={`article-section-${s.heading.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <h2 className="font-serif text-2xl tracking-tight text-atlantic sm:text-3xl">{s.heading}</h2>
              {s.paragraphs.map((p, i) => (
                <p key={i} className="mt-5 text-base leading-relaxed text-slate-700 sm:text-lg">{p}</p>
              ))}
            </section>
          ))}

          <blockquote data-testid="article-pull-quote" className="border-l-2 border-gold py-2 pl-8">
            <p className="font-serif text-2xl italic leading-snug text-atlantic sm:text-3xl">
              “How does diplomatic symbolism become measurable cooperation?”
            </p>
            <cite className="mt-4 block text-xs font-semibold uppercase not-italic tracking-[0.25em] text-slate-500">
              The central question of this analysis
            </cite>
          </blockquote>

          {BODY_SECTIONS.slice(2).map((s) => (
            <section key={s.heading} data-testid={`article-section-${s.heading.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <h2 className="font-serif text-2xl tracking-tight text-atlantic sm:text-3xl">{s.heading}</h2>
              {s.paragraphs.map((p, i) => (
                <p key={i} className="mt-5 text-base leading-relaxed text-slate-700 sm:text-lg">{p}</p>
              ))}
            </section>
          ))}
        </div>

        <section className="mt-16 border-t border-atlantic/10 pt-10" aria-label="Primary sources and method">
          <h2 className="font-serif text-2xl tracking-tight text-atlantic">Primary sources and verification</h2>
          <p className="mt-4 text-sm leading-relaxed text-slate-600">This independent analysis uses attributable government records available by 15 October 2026. A reported plan is not treated as a completed outcome. No interviews are claimed for this document-based analysis.</p>
          <ol className="mt-5 list-none space-y-3 text-sm leading-relaxed">
            {SOURCES.map((source) => (
              <li key={source.url}><a href={source.url} target="_blank" rel="noopener noreferrer" className="text-ocean underline underline-offset-2 hover:text-atlantic">{source.label} ↗</a></li>
            ))}
          </ol>
        </section>

        <div className="mt-16 border-t border-atlantic/10 pt-12">
          <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-500">Related Stories</p>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
            {RELATED.map((r) => (
              <Link
                key={r.title}
                to={r.to}
                data-testid={`article-related-${r.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="group border border-atlantic/10 bg-white p-6 transition-colors hover:border-gold"
              >
                <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-ocean">{r.label}</p>
                <p className="mt-3 font-serif text-base leading-snug text-atlantic group-hover:text-ocean sm:text-lg">{r.title}</p>
                <ArrowUpRight size={15} className="mt-4 text-atlantic/40 transition-colors group-hover:text-gold" />
              </Link>
            ))}
          </div>
        </div>
      </article>

      <Newsletter />
    </main>
  );
}
